import { createContext } from 'preact'
import { useContext, useEffect, useRef, useState } from 'preact/hooks'
import { HEARTBEAT_SECONDS } from '../limits'
import { streamOnAir } from '../player/source'
import { createRoomConnection } from '../realtime/roomConnection'
import type { RoomConnection } from '../realtime/roomConnection'
import { createRoomStore, runsTheRoom } from '../store/roomStore'
import type { RoomStore } from '../store/roomStore'
import { scheduleOfRoom, sessionPhase } from '../time'
import { isRoomRequestError } from '../transport/roomTransport'
import type { RoomTransport } from '../transport/roomTransport'
import type { RoomJoin, RoomMessage, RoomState } from '../types'

/** One open room: its state, and the way to reach it. Everything shared inside a room reads this. */
export interface RoomSession {
    store: RoomStore
    transport: RoomTransport
}

export const RoomSessionContext = createContext<RoomSession | null>(null)

export function useRoomSession(): RoomSession {
    const session = useContext(RoomSessionContext)
    if (!session) throw new Error('useRoomSession must be used inside a room, under RoomSessionContext.')
    return session
}

/** How often whoever runs the room rereads its people, presenters and poll runs, besides on every change it hears of. */
export const MODERATOR_STATE_REFRESH_MS = 15_000

/** How often a viewer's page whose realtime link is down asks for the room while its stage waits on the stream. */
export const STREAM_WAIT_REJOIN_MS = HEARTBEAT_SECONDS * 1000

export interface RoomSessionOptions {
    /** What this page makes of a join it asks for again: the viewer page adds the live stream it was baked with. */
    adaptJoin?: (join: RoomJoin) => RoomJoin
    /** A join asked for again says this link belongs at the door now: bound to another device, or its registration gone. */
    onLostSeat?: () => void
    /** Builds the realtime link; a test passes a fake. */
    connect?: typeof createRoomConnection
}

/** Past the start with a stream that is not on the air: the viewer's stage holds on "Starting" until it is. */
function waitingOnStream(room: RoomState, nowMs: number): boolean {
    return sessionPhase(scheduleOfRoom(room), room, nowMs) === 'live' && !streamOnAir(room.VideoState)
}

/**
 * Opens the room a join let this page into: the store, the realtime link feeding it, and, for whoever runs the room,
 * the people list kept fresh: every 15 s, on `PeopleChanged`, and when a poll launches or closes, since the poll runs
 * live in the same read. Closed again on unmount.
 *
 * The room's state changes only by broadcast, and a page hears none sent before its link joined the room's groups or
 * while it was down. So every time the link comes up, the first time too, the page asks for its join again and takes
 * the room and playback from it; after a drop, its messages as well. A viewer whose link stays down asks again while
 * its stage waits on a stream that should be on the air, so the video never hangs on one broadcast; and a viewer asks
 * when the replay comes out, for the token only a join carries.
 *
 * A join asked for again that fails keeps what is on screen: the next connect, or the next event, mends it.
 */
export function useRoomSessionSetup(transport: RoomTransport, join: RoomJoin, options: RoomSessionOptions = {}): RoomSession {
    const [session] = useState<RoomSession>(() => ({ store: createRoomStore(join), transport }))
    // Read when a join comes back, not captured: a caller passes these inline, and they must not reopen the link.
    const latest = useRef(options)
    latest.current = options
    const connect = options.connect ?? createRoomConnection

    useEffect(() => {
        const { store } = session
        let closed = false
        const loadModeratorState = () => {
            if (!runsTheRoom(join.Role)) return
            transport.moderatorState().then(
                state => store.dispatch({ type: 'moderatorStateLoaded', state }),
                () => store.dispatch({ type: 'moderatorStateFailed' }),
            )
        }
        const rejoin = (withMessages: boolean): Promise<void> =>
            transport.join().then(
                fresh => {
                    if (closed) return
                    if (fresh.RequiresDoor) {
                        latest.current.onLostSeat?.()
                        return
                    }
                    const adapted = latest.current.adaptJoin?.(fresh) ?? fresh
                    store.dispatch({ type: 'rejoined', join: adapted })
                    if (withMessages) store.dispatch({ type: 'historyLoaded', messages: adapted.Messages })
                },
                (error: unknown) => {
                    if (!closed && isRoomRequestError(error, 'not-found')) latest.current.onLostSeat?.()
                },
            )
        // Only a join mints the replay's token, so a viewer's page open when the replay comes out asks for one.
        let askingForReplay = false
        const askForReplay = () => {
            if (askingForReplay || runsTheRoom(join.Role) || store.state.value.playback.ReplayToken) return
            askingForReplay = true
            void rejoin(false).finally(() => { askingForReplay = false })
        }

        const connection: RoomConnection = connect({
            roomId: join.Room.RoomId,
            negotiate: () => transport.negotiate(),
            handlers: {
                // Whoever runs the room is in both groups, so a message visible to everyone reaches them twice, in either
                // order. Only the moderator group's copy is whole: the viewer copy shows a dismissed moved question as open.
                MessageUpserted: payload => {
                    if (runsTheRoom(join.Role) && !payload.ForModerators) return
                    store.dispatch({ type: 'messageUpserted', message: payload.Message as RoomMessage })
                },
                MessageRemoved: payload => store.dispatch({ type: 'messageRemoved', messageId: payload.MessageId }),
                ModeratorAnswering: payload =>
                    store.dispatch({ type: 'answering', messageId: payload.MessageId, name: payload.Name, active: payload.Active }),
                PollLaunched: payload => {
                    store.dispatch({ type: 'pollLaunched', poll: payload.Poll })
                    loadModeratorState()
                },
                PollClosed: payload => {
                    store.dispatch({ type: 'pollClosed', pollId: payload.PollId })
                    loadModeratorState()
                },
                PollTally: payload => store.dispatch({ type: 'pollTally', pollId: payload.PollId, counts: payload.Counts, total: payload.Total }),
                RoomStateChanged: payload => {
                    store.dispatch({ type: 'roomStateChanged', room: payload.Room as RoomState })
                    if (payload.Room.ReplayAvailable) askForReplay()
                },
                PeopleChanged: loadModeratorState,
            },
            onStatus: status => store.dispatch({ type: 'connection', status }),
            onConnected: recovered => {
                void rejoin(recovered)
                if (recovered) loadModeratorState()
            },
        })

        void connection.start()
        loadModeratorState()
        const refresh = runsTheRoom(join.Role) ? setInterval(loadModeratorState, MODERATOR_STATE_REFRESH_MS) : undefined
        const streamWait = runsTheRoom(join.Role) ? undefined : setInterval(() => {
            const { connection: link, room } = store.state.value
            if (link !== 'connected' && waitingOnStream(room, Date.now())) void rejoin(false)
        }, STREAM_WAIT_REJOIN_MS)
        return () => {
            closed = true
            clearInterval(refresh)
            clearInterval(streamWait)
            void connection.stop()
        }
    }, [session, transport, join, connect])

    return session
}
